import { Search, Loader2, AlertCircle } from 'lucide-react';
import { SearchResultItem } from './SearchResultItem';
import type { TaskSearchResult } from '@/types';

interface SearchResultsProps {
  results: TaskSearchResult[];
  total: number;
  query?: string;
  isLoading: boolean;
  error?: Error | null;
  hasSearched: boolean;
}

export function SearchResults({
  results,
  total,
  query,
  isLoading,
  error,
  hasSearched,
}: SearchResultsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-10 w-10 text-destructive/60 mb-3" />
        <p className="text-muted-foreground">Failed to load search results</p>
        <p className="text-sm text-muted-foreground/70 mt-1">Please try again</p>
      </div>
    );
  }

  if (!hasSearched) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground">Enter a search term or apply filters</p>
        <p className="text-sm text-muted-foreground/70 mt-1">
          Search across all tasks you have access to
        </p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground">
          {query ? `No results found for "${query}"` : 'No results match your filters'}
        </p>
        <p className="text-sm text-muted-foreground/70 mt-1">
          Try adjusting your search terms or filters
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Found {total} result{total !== 1 ? 's' : ''}
        {query && ` for "${query}"`}
      </p>
      <div className="space-y-3">
        {results.map((task) => (
          <SearchResultItem key={task.id} task={task} />
        ))}
      </div>
    </div>
  );
}
